import { selectCustomers } from '../../../store/slices/customers';
import { SHOW_TASK_ASSIGNEE_ROLES } from '../taskBoardConfig';
import { useTaskTranslation } from '../hooks/useTaskTranslation';
import { useAppSelector } from '../../../store/store';
import { selectPersonsInLocation } from '../../../store/slices/auth';
import {
  findTaskCustomer,
  taskAssigneeDesignation,
  taskDesignationLabel,
  taskStatusLabel,
} from '../taskUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Task, TASK_STATUSES, TaskStatus } from '../../../store/slices/tasks';
import { TaskPriorityIndicator } from './TaskPriorityIndicator';
import styles from './TaskTable.module.css';

interface Props {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  canUpdate: boolean;
  pendingTaskIds: number[];
  onStatusChange: (taskId: number, status: TaskStatus) => Promise<void>;
}

type SortKey =
  | 'task_id'
  | 'title'
  | 'type'
  | 'status'
  | 'priority'
  | 'person_name_assign'
  | 'customer_name'
  | 'datetime_start'
  | 'datetime_finish';
const columns: { label: string; key?: SortKey }[] = [
  { label: 'task_board.id', key: 'task_id' },
  { label: 'task_board.title', key: 'title' },
  { label: 'task_board.type', key: 'type' },
  { label: 'task_board.status', key: 'status' },
  { label: 'task_board.priority', key: 'priority' },
  { label: 'task_board.assignee', key: 'person_name_assign' },
  { label: 'task_board.customer', key: 'customer_name' },
  { label: 'task_board.start_date_and_time', key: 'datetime_start' },
  { label: 'task_board.end_date_and_time', key: 'datetime_finish' },
  { label: 'task_board.action' },
];
const PAGE_SIZE = 10;
const formatDateTime = (value: string | undefined, language: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
};

export const TaskTable = ({
  tasks,
  onTaskClick,
  canUpdate,
  pendingTaskIds,
  onStatusChange,
}: Props) => {
  const { translations, taskText, currentLang } = useTaskTranslation();
  const persons = useAppSelector(selectPersonsInLocation) ?? [];
  const customers = useAppSelector(selectCustomers);
  const customerName = useCallback(
    (task: Task) => {
      const customer = findTaskCustomer(task.customer_id, customers);
      return [customer?.person_firstname, customer?.person_lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
    },
    [customers]
  );
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'task_id',
    direction: 'asc',
  });
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [tasks]);
  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((first, second) => {
        const left = sort.key === 'customer_name' ? customerName(first) : first[sort.key];
        const right = sort.key === 'customer_name' ? customerName(second) : second[sort.key];
        // Missing values stay at the end in either direction.
        if (left == null || left === '') return right == null || right === '' ? 0 : 1;
        if (right == null || right === '') return -1;
        const comparison =
          sort.key === 'priority'
            ? ['low', 'medium', 'high'].indexOf(String(left)) -
            ['low', 'medium', 'high'].indexOf(String(right))
            : sort.key === 'task_id'
              ? Number(left) - Number(right)
              : sort.key.startsWith('datetime_')
                ? (Date.parse(String(left)) || 0) - (Date.parse(String(right)) || 0)
                : String(left).localeCompare(String(right), undefined, {
                  numeric: true,
                  sensitivity: 'base',
                });
        return sort.direction === 'asc' ? comparison : -comparison;
      }),
    [tasks, sort, customerName]
  );
  const pageCount = Math.max(1, Math.ceil(tasks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageTasks = sortedTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section className={styles.container} aria-label={translations('task_board.all_tasks')}>
      <div
        className={styles.scrollContainer}
        tabIndex={0}
        role="region"
        aria-label={translations('task_board.tasks_table')}
      >
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(({ label, key }) => (
                <th
                  key={label}
                  scope="col"
                  aria-sort={
                    key
                      ? sort.key === key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  }
                >
                  {key ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSort({
                          key,
                          direction: sort.key === key && sort.direction === 'asc' ? 'desc' : 'asc',
                        });
                        setPage(1);
                      }}
                    >
                      {translations(label)}{' '}
                      <span
                        aria-hidden="true"
                        className={sort.key === key ? styles.sorted : styles.sortIcon}
                      >
                        {sort.key === key ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    translations(label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageTasks.map((task, index) => {
              const status = task.status || '';
              const designation = taskAssigneeDesignation(task, persons);
              const pending = task.task_id !== undefined && pendingTaskIds.includes(task.task_id);
              const statusClass =
                status === 'in progress'
                  ? styles.inProgress
                  : status === 'open'
                    ? styles.open
                    : status === 'closed'
                      ? styles.closed
                      : styles.discarded;
              return (
                <tr key={task.task_id ?? index} aria-busy={pending}>
                  <td>
                    <button className={styles.idButton} onClick={() => onTaskClick(task)}>
                      {task.task_id !== undefined ? `ECO-${task.task_id}` : '—'}
                    </button>
                  </td>
                  <td className={styles.title}>
                    <button onClick={() => onTaskClick(task)}>
                      {task.title || translations('task_board.untitled_task')}
                    </button>
                  </td>
                  <td className={styles.type}>{task.type || '—'}</td>
                  <td>
                    <select
                      className={`${styles.status} ${statusClass}`}
                      aria-label={taskText('task_board.status_for_eco_id', {
                        id: task.task_id ?? '',
                      })}
                      value={status}
                      disabled={!canUpdate || pending || task.task_id === undefined}
                      onChange={(event) => {
                        if (task.task_id !== undefined)
                          void onStatusChange(task.task_id, event.target.value as TaskStatus);
                      }}
                    >
                      {!TASK_STATUSES.some((value) => value === status) && (
                        <option value={status}>
                          {status ? taskStatusLabel(status, translations) : '—'}
                        </option>
                      )}
                      {TASK_STATUSES.map((value) => (
                        <option key={value} value={value}>
                          {taskStatusLabel(value, translations)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {task.priority ? (
                      <TaskPriorityIndicator priority={task.priority} showLabel />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {task.person_name_assign ? (
                      <div>
                        <span className={styles.person}>{task.person_name_assign}</span>
                        {SHOW_TASK_ASSIGNEE_ROLES && designation && (
                          <small className={styles.designation}>
                            {taskDesignationLabel(designation, translations)}
                          </small>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={styles.customer}>
                    {customerName(task) || '—'}
                    {task.customer_id !== undefined && <small>#{task.customer_id}</small>}
                  </td>
                  <td className={styles.date}>
                    {formatDateTime(task.datetime_start, currentLang)}
                  </td>
                  <td className={styles.date}>
                    {formatDateTime(task.datetime_finish, currentLang)}
                  </td>
                  <td>
                    <button
                      className={styles.viewButton}
                      aria-label={taskText('task_board.view_eco_id', { id: task.task_id ?? '' })}
                      onClick={() => onTaskClick(task)}
                    >
                      {translations('task_board.view')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {pageTasks.length === 0 && (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  {translations('task_board.no_tasks_to_display')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className={styles.footer}>
        <span>
          {tasks.length
            ? taskText('task_board.start_end_of_count_tasks', {
              start: (currentPage - 1) * PAGE_SIZE + 1,
              end: Math.min(currentPage * PAGE_SIZE, tasks.length),
              count: tasks.length,
            })
            : translations('task_board.0_tasks')}
        </span>
        <nav
          className={styles.pagination}
          aria-label={translations('task_board.task_table_pagination')}
        >
          <button
            aria-label={translations('task_board.first_page')}
            disabled={currentPage === 1}
            onClick={() => setPage(1)}
          >
            «
          </button>
          <button
            aria-label={translations('task_board.previous_page')}
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
          >
            ‹
          </button>
          <span aria-current="page">
            {currentPage} / {pageCount}
          </span>
          <button
            aria-label={translations('task_board.next_page')}
            disabled={currentPage === pageCount}
            onClick={() => setPage(currentPage + 1)}
          >
            ›
          </button>
          <button
            aria-label={translations('task_board.last_page')}
            disabled={currentPage === pageCount}
            onClick={() => setPage(pageCount)}
          >
            »
          </button>
        </nav>
      </footer>
    </section>
  );
};
